import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Div } from '@pinpoint/design-kit';
import isEqual from 'lodash.isequal';

import { pxHistogramsPropType, pxScoresPropType, stylePropType } from 'src/PropTypes';
import { EventEmitter } from 'src/components';
import { Style1 as Style } from 'src/styles';
import TimedPopup from './timed_popup';
import TextualContent from './textual';
import ScoreChartContainer from './score';
import OceanChart from './ocean';
import Takeaways from './takeaways';

const FACTOR = 'factor';
const FACET = 'facet';
const PSYCHOMETRIC = 'psychometric';
const OPENNESS = 'Openness';
const CONSCIENTUOUSNESS = 'Conscientiousness';
const EXTRAVERSION = 'Extraversion';
const AGREEABLENESS = 'Agreeableness';
const NEUROTICISM = 'Neuroticism';

/*
 * NOTE: Might be needed later
 * const FACTOR_CODE_MAP = {
 *   Openness: 'px_dim_o',
 *   Conscientiousness: 'px_dim_c',
 *   Extraversion: 'px_dim_e',
 *   Agreeableness: 'px_dim_a',
 *   Neuroticism: 'px_dim_n',
 * };
 */
export default class Ocean extends Component {
  constructor(props) {
    super(props);
    const { objects, personalityExplorerMode } = this.props;
    this.selectedIndex = 0;
    const series = Ocean.getSeries(objects);
    const traits = Ocean.getTraits(objects[this.selectedIndex]);
    const factorsAndFacets = Ocean.getFactorsAndFacetsSeries(series, traits, objects);
    const top7SeriesAndLabels = Ocean.getTop7SeriesAndLabels(objects[this.selectedIndex]);
    const actionableFactorsAndFacets = Ocean.getActionableFactorsAndFacets(objects[this.selectedIndex]);
    this.eventEmitter = new EventEmitter();
    // Find the maximum index for Openness factor
    this.selectedFactor = actionableFactorsAndFacets.actionableFactors[0].trait.code;
    this.selectedFacet = top7SeriesAndLabels.top7Labels[0].code;
    this.selectedTrait = traits.find(({ code }) => code === this.selectedFactor);
    const { score: selectedScore } = objects[this.selectedIndex].pxScores.find(
      ({ trait: { code } }) => code === this.selectedFactor
    );
    const { pxHistograms, refresh } = objects[this.selectedIndex];
    this.selectedScore = selectedScore;
    this.state = {
      objects,
      personalityExplorerMode,
      refresh,
      pxHistograms,
      ...factorsAndFacets,
      ...top7SeriesAndLabels,
      ...actionableFactorsAndFacets,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (!isEqual(props.objects, state.objects)) {
      const { objects } = props;
      const selectedIndex = 0;
      const traits = Ocean.getTraits(objects[selectedIndex]);
      const series = Ocean.getSeries(objects);
      const factorsAndFacets = Ocean.getFactorsAndFacetsSeries(series, traits, objects);
      const top7SeriesAndLabels = Ocean.getTop7SeriesAndLabels(objects[selectedIndex]);
      const actionableFactorsAndFacets = Ocean.getActionableFactorsAndFacets(objects[selectedIndex]);
      const { pxHistograms, refresh } = objects[selectedIndex];
      return {
        objects,
        pxHistograms,
        refresh,
        ...factorsAndFacets,
        ...top7SeriesAndLabels,
        ...actionableFactorsAndFacets,
      };
    }
    return null;
  }

  static getTraits = (object) => object.pxScores.map(({ trait }) => trait);

  static getSeries = (objects) =>
    objects.map(({ name, pxScores }) => ({
      name,
      values: pxScores.map(({ score }) => score),
    }));

  static getFactorsAndFacetsSeries = (series, traits, objects) => {
    const oceanFactorLabels = [];
    const oceanFactorSeries = [];
    const OFacetLabels = [];
    const OFacetSeries = [];
    const CFacetLabels = [];
    const CFacetSeries = [];
    const EFacetLabels = [];
    const EFacetSeries = [];
    const AFacetLabels = [];
    const AFacetSeries = [];
    const NFacetLabels = [];
    const NFacetSeries = [];
    for (let j = 0; j < series.length; j += 1) {
      oceanFactorSeries.push({
        name: series[j].name,
        values: [],
      });
      OFacetSeries.push({
        name: series[j].name,
        values: [],
      });
      CFacetSeries.push({
        name: series[j].name,
        values: [],
      });
      EFacetSeries.push({
        name: series[j].name,
        values: [],
      });
      AFacetSeries.push({
        name: series[j].name,
        values: [],
      });
      NFacetSeries.push({
        name: series[j].name,
        values: [],
      });
      for (let i = 0; i < traits.length; i += 1) {
        if (traits[i].subType === FACTOR) {
          // OCEAN Factor
          oceanFactorLabels.push(objects[j].pxScores[i].trait);
          oceanFactorSeries[j].values.push(series[j].values[i]);
        } else if (traits[i].subType === FACET) {
          switch (traits[i].factor) {
            case OPENNESS: {
              OFacetLabels.push(objects[j].pxScores[i].trait);
              OFacetSeries[j].values.push(series[j].values[i]);
              break;
            }
            case CONSCIENTUOUSNESS: {
              CFacetLabels.push(objects[j].pxScores[i].trait);
              CFacetSeries[j].values.push(series[j].values[i]);
              break;
            }
            case EXTRAVERSION: {
              EFacetLabels.push(objects[j].pxScores[i].trait);
              EFacetSeries[j].values.push(series[j].values[i]);
              break;
            }
            case AGREEABLENESS: {
              AFacetLabels.push(objects[j].pxScores[i].trait);
              AFacetSeries[j].values.push(series[j].values[i]);
              break;
            }
            case NEUROTICISM: {
              NFacetLabels.push(objects[j].pxScores[i].trait);
              NFacetSeries[j].values.push(series[j].values[i]);
              break;
            }
            default:
          }
        }
      }
    }
    return {
      oceanFactorLabels,
      oceanFactorSeries,
      OFacetLabels,
      OFacetSeries,
      CFacetLabels,
      CFacetSeries,
      EFacetLabels,
      EFacetSeries,
      AFacetLabels,
      AFacetSeries,
      NFacetLabels,
      NFacetSeries,
    };
  };

  static getTop7SeriesAndLabels = (object) => {
    const psycometricPxScores = object.pxScores.filter(
      (pxScore) => pxScore.trait.traitType === PSYCHOMETRIC && pxScore.trait.subType === FACET
    );
    psycometricPxScores.sort((a, b) => Math.abs(50 - b.score) - Math.abs(50 - a.score));
    const top7PxScores = psycometricPxScores.slice(0, 7);
    const top7Labels = top7PxScores.map(({ trait }) => trait);
    const top7Series = [];
    top7Series.push({
      name: object.name,
      values: top7PxScores.map(({ score }) => score),
    });
    return {
      top7Labels,
      top7Series,
    };
  };

  static getActionableFactorsAndFacets = (object) => {
    let actionableFactors = object.pxScores
      .filter((pxScore) => pxScore.trait.traitType === PSYCHOMETRIC)
      .filter((pxScore) => pxScore.trait.subType === FACTOR);
    actionableFactors.sort((a, b) => Math.abs(50 - b.score) - Math.abs(50 - a.score));
    actionableFactors = actionableFactors.slice(0, 2);
    let actionableFacets = object.pxScores
      .filter((pxScore) => pxScore.trait.traitType === PSYCHOMETRIC)
      .filter((pxScore) => pxScore.trait.subType === FACET);
    actionableFacets.sort((a, b) => Math.abs(50 - b.score) - Math.abs(50 - a.score));
    actionableFacets = actionableFacets.slice(0, 3);
    return {
      actionableFactors,
      actionableFacets,
    };
  };

  render() {
    const {
      objects,
      style,
      styleOverride,
      minH,
      minW,
      enableTimedPopup,
      timedPopupDelay,
      timedPopupContent,
      onClickTimedPopup,
      isTimedPopupBlocked,
      onBlockTimedPopup,
      timedPopupOkText,
      timedPopupCancelText,
      hideMarketingTakeaways,
    } = this.props;
    const {
      personalityExplorerMode,
      oceanFactorLabels,
      oceanFactorSeries,
      OFacetLabels,
      OFacetSeries,
      CFacetLabels,
      CFacetSeries,
      EFacetLabels,
      EFacetSeries,
      AFacetLabels,
      AFacetSeries,
      NFacetLabels,
      NFacetSeries,
      top7Labels,
      top7Series,
      actionableFactors,
      actionableFacets,
      pxHistograms,
      refresh,
    } = this.state;
    if (objects.length === 0) {
      return <div>No Data</div>;
    }
    return (
      <Div h="100%" minH={minH} w="100%" minW={minW} bg="secondary" m="0" p="0">
        <TimedPopup
          enabled={enableTimedPopup}
          isBlocked={() => isTimedPopupBlocked()}
          onClick={() => {
            onClickTimedPopup();
          }}
          delay={timedPopupDelay}
          content={timedPopupContent}
          block={() => {
            onBlockTimedPopup();
          }}
          okText={timedPopupOkText}
          cancelText={timedPopupCancelText}
        />
        {personalityExplorerMode || hideMarketingTakeaways ? (
          <Div h="100%">
            <Row p={{ b: '1.25rem' }} minH="350px">
              <Col size={{ xs: '12', lg: '8' }} gutterWidth="1.25rem">
                <Div
                  minH="350px"
                  h="100%"
                  w="100%"
                  rounded="md"
                  p={{ x: '1.5rem', y: '1.25rem' }}
                  bg="white"
                  data-test="desc-div"
                >
                  <TextualContent
                    eventEmitter={this.eventEmitter}
                    name={objects[0].name}
                    selectedTrait={this.selectedTrait}
                    selectedScore={this.selectedScore}
                    style={style}
                    styleOverride={styleOverride}
                  />
                </Div>
              </Col>
              <Col size={{ xs: '12', lg: '4' }} gutterWidth="1.25rem">
                <Div
                  minH="350px"
                  pos="relative"
                  h="100%"
                  w="100%"
                  rounded="md"
                  p={{ x: '1.5rem', y: '1.25rem' }}
                  bg="white"
                >
                  <ScoreChartContainer
                    eventEmitter={this.eventEmitter}
                    selectedTrait={this.selectedTrait}
                    selectedScore={this.selectedScore}
                    style={style}
                    styleOverride={styleOverride}
                    pxHistograms={pxHistograms}
                    refresh={refresh}
                  />
                </Div>
              </Col>
            </Row>
            <Row minH="calc(100% - 350px - 1.25rem)" p={{ x: '0' }}>
              <Col size={{ xs: '12', lg: '12' }} pos="relative">
                <Div bg=" white" padding={{ x: '1.5rem', y: '1.25rem' }} rounded="md" h="100%">
                  <OceanChart
                    chart="OCEAN"
                    objects={objects}
                    eventEmitter={this.eventEmitter}
                    selectedIndex={this.selectedIndex}
                    OFacetLabels={OFacetLabels}
                    OFacetSeries={OFacetSeries}
                    CFacetLabels={CFacetLabels}
                    CFacetSeries={CFacetSeries}
                    EFacetLabels={EFacetLabels}
                    EFacetSeries={EFacetSeries}
                    AFacetLabels={AFacetLabels}
                    AFacetSeries={AFacetSeries}
                    NFacetLabels={NFacetLabels}
                    NFacetSeries={NFacetSeries}
                    top7Labels={top7Labels}
                    top7Series={top7Series}
                    style={style}
                    selectedFacet={this.selectedFacet}
                    selectedFactor={this.selectedFactor}
                    selectedTop7Factor={top7Labels[0].code}
                    oceanFactorLabels={oceanFactorLabels}
                    oceanFactorSeries={oceanFactorSeries}
                    styleOverride={styleOverride}
                  />
                </Div>
              </Col>
            </Row>
          </Div>
        ) : (
          <Takeaways
            name={objects[0].name}
            eventEmitter={this.eventEmitter}
            actionableFactors={actionableFactors}
            actionableFacets={actionableFacets}
            toggle={() => {
              this.setState((prevState) => ({
                personalityExplorerMode: !prevState.personalityExplorerMode,
              }));
            }}
            styleOverride={styleOverride}
          />
        )}
      </Div>
    );
  }
}

Ocean.propTypes = {
  objects: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      pxScores: pxScoresPropType,
      pxHistograms: pxHistogramsPropType,
      refresh: PropTypes.shape({
        pxHistograms: pxHistogramsPropType,
      }),
    })
  ).isRequired,
  style: stylePropType,
  styleOverride: PropTypes.shape({}),
  minH: PropTypes.string,
  minW: PropTypes.string,
  enableTimedPopup: PropTypes.bool,
  timedPopupDelay: PropTypes.number,
  timedPopupContent: PropTypes.node,
  onClickTimedPopup: PropTypes.func,
  isTimedPopupBlocked: PropTypes.func,
  onBlockTimedPopup: PropTypes.func,
  timedPopupOkText: PropTypes.string,
  timedPopupCancelText: PropTypes.string,
  personalityExplorerMode: PropTypes.bool,
  hideMarketingTakeaways: PropTypes.bool,
};

Ocean.defaultProps = {
  personalityExplorerMode: false,
  style: Style,
  styleOverride: {
    background: Style.global.backgroundColor,
    border: Style.global.backgroundColor,
  },
  minH: '100%',
  minW: '100%',
  enableTimedPopup: false,
  timedPopupDelay: 2000,
  timedPopupContent: <p>Under construction</p>,
  onClickTimedPopup: () => null,
  isTimedPopupBlocked: () => false,
  onBlockTimedPopup: () => null,
  timedPopupCancelText: 'Cancel',
  timedPopupOkText: 'Learn More',
  hideMarketingTakeaways: true,
};
